(() => {
  // ====== Config ======
  const UI_BATCH  = 20;
  const FETCH_LIM = 50;  // Shopify max per for-loop is 50
  const COLLECTION_HANDLE = 'products';
  const OPENERS_SELECTOR =
    '.open-search-overlay-icon, .js-open-search, .desktop-search-trigger, .search-box';

  // ====== State ======
  let all = [];
  let filtered = [];
  let shown = 0;
  let loading = false;
  let loaded  = false;
  let pushedHistory = false; // 1 state per open-cyclus

  // ====== Helpers ======
  const esc  = (s='') => String(s);
  const norm = s => esc(s || '').replace(/\\/g,'');
  const debounce = (fn, ms=200) => { let t; return (...a)=>{ clearTimeout(t); t=setTimeout(()=>fn(...a),ms); }; };
  const isMobile = () => window.matchMedia('(max-width: 767px)').matches;

  // Lazy getters
  const $drawer  = () => document.getElementById('sm-search-drawer');
  const $panel   = () => $drawer()?.querySelector('.sm-search-panel');
  const $listEl  = () => document.getElementById('sm-search-list');
  const $inputEl = () => document.getElementById('sm-search-input');

  const rowHTML = (p) => `
    <a class="sm-row" href="${esc(p.url)}" role="option">
      <img class="sm-thumb" src="${norm(p.image)}" alt="">
      <div class="sm-meta">
        <p class="sm-title">${esc(p.title)}</p>
        <p class="sm-vendor">${esc(p.vendor || '')}</p>
      </div>
    </a>
  `;

  function clearList(){
    const listEl = $listEl();
    if (listEl) listEl.innerHTML = '';
  }

  function renderMore(n = UI_BATCH){
    const listEl = $listEl();
    if (!listEl || !filtered.length) return;
    const next = filtered.slice(shown, shown + n);
    if (!next.length) return;
    listEl.insertAdjacentHTML('beforeend', next.map(rowHTML).join(''));
    shown += next.length;
  }

  // 22 okt 2026: zoeken zonder accenten/leestekens ("acqua di gio" vindt Acqua di Giò, "dolce gabbana" vindt Dolce&Gabbana)
  const fold = (s='') => String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g,'').replace(/[^a-z0-9]+/g,' ').trim();
  // Veelgebruikte merknamen die in de shop anders heten
  const ALIASES = { 'paco rabanne': 'rabanne', 'ysl': 'yves saint laurent', 'd g': 'dolce gabbana', 'dg': 'dolce gabbana', 'jpg': 'jean paul gaultier', 'ck': 'calvin klein' };

  function applyFilter(q){
    let ql = fold(q || '');
    Object.keys(ALIASES).forEach(k => {
      if (ql === k || ql.startsWith(k + ' ')) ql = ALIASES[k] + ql.slice(k.length);
    });
    filtered = !ql
      ? all.slice()
      : all.filter(p => fold((p.title || '') + ' ' + (p.vendor || '')).includes(ql));
    shown = 0;
    clearList();
    if (ql && !filtered.length) {
      const listEl = $listEl();
      if (listEl) {
        const safe = String(q).trim().replace(/[<>&"]/g, '');
        listEl.innerHTML = `
          <div class="sm-search-empty" role="status">
            <p class="sm-search-empty__titel">Geen parfum gevonden voor "${safe}"</p>
            <p class="sm-search-empty__tekst">Zoek op de naam van het parfum of het merk. Twijfel je? Bekijk alle geuren of doe de quiz.</p>
            <div class="sm-search-empty__knoppen">
              <a href="/collections/products" class="sm-search-empty__knop sm-search-empty__knop--zwart">Bekijk alle geuren</a>
              <a href="/pages/parfumquiz" class="sm-search-empty__knop">Doe de parfumquiz</a>
            </div>
          </div>`;
      }
      return;
    }
    renderMore();
  }

  function sortAll(){
    all.sort((a, b) =>
      (a.title || '').localeCompare(b.title || '', 'nl', { sensitivity: 'base' })
    );
  }

  function openDrawer(){
    const drawer = $drawer();
    if (!drawer) return;

    // push één history state per open
    if (!pushedHistory) {
      history.pushState({ smSearchOpen: true }, '', '');
      pushedHistory = true;
    }

    // zichtbaar + animatie
    drawer.classList.remove('hidden');
    // force reflow zodat transition altijd triggert
    // eslint-disable-next-line no-unused-expressions
    drawer.offsetHeight;
    drawer.classList.add('is-open');

    drawer.setAttribute('aria-hidden','false');
    document.documentElement.classList.add('sm-no-scroll');

    // niet focussen op mobiel (keyboard pas bij tik in het veld)
    if (!isMobile()) $inputEl()?.focus();
  }

  function closeDrawer({ fromPop = false } = {}){
    const drawer = $drawer();
    const panel  = $panel();
    if (!drawer || !panel) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden','true');
    document.documentElement.classList.remove('sm-no-scroll');

    const inp = $inputEl();
    if (inp) inp.value = '';
    shown = 0;
    clearList();

    // verberg pas na animatie
    const onEnd = (e) => {
      if (e.propertyName !== 'transform') return;
      drawer.classList.add('hidden');
      panel.removeEventListener('transitionend', onEnd);
    };
    panel.addEventListener('transitionend', onEnd);

    // popstate heeft het “terug” al afgehandeld -> flag resetten
    if (fromPop) pushedHistory = false;
  }

  // UI vraagt sluiten -> probeer via history (voor Android back-gedrag)
  function requestClose(){
    const drawer = $drawer();
    if (!drawer || !drawer.classList.contains('is-open')) return;

    if (pushedHistory) {
      history.back(); // triggert popstate -> closeDrawer({fromPop:true})
    } else {
      // fallback (zou zelden gebeuren)
      closeDrawer({ fromPop: true });
    }
  }

  // ====== Data ======
  async function loadFeed(){
    if (loading || loaded) return;
    loading = true;
    try{
      const listEl = $listEl();
      if (listEl) listEl.innerHTML = '<div class="sm-row" style="justify-content:center">Bezig met laden…</div>';

      let nextPage = 1;
      let keepLoading = true;

      while (keepLoading) {
        // Use search.ajax template which has metafield support
        const url = `/search?view=ajax&type=product&q=*&limit=${FETCH_LIM}&page=${nextPage}`;
        const res = await fetch(url, { headers: { 'Accept': 'application/json' }});
        if (!res.ok) throw new Error(`HTTP ${res.status} @ ${url}`);
        const data = await res.json();

        console.log(`[search] page ${nextPage}/${data.pages}: ${data.results?.length || 0} items, total: ${data.results_count}`);

        const items = Array.isArray(data.results) ? data.results : [];
        items.forEach(it => all.push({
          title:  it.title || '',
          handle: it.handle || '',
          vendor: it.vendor || '',
          image:  it.image || '',
          url:    it.url || `/products/${it.handle}`
        }));
        sortAll(); // sorteer alfabetisch op titel na elke batch

        // Show first batch immediately
        if (nextPage === 1) {
          applyFilter($inputEl()?.value || '');
        }

        // Continue loading while backend reports a next_page
        const np = data.next_page;
        if (np) {
          nextPage = np;
          // Re-apply filter to include newly loaded items in current query
          applyFilter($inputEl()?.value || '');
        } else {
          keepLoading = false;
        }
      }

      console.log(`[search] Loaded all ${all.length} products`);

      loaded = true;
    } catch(err){
      console.error('[search] load error', err);
      const listEl = $listEl();
      if (listEl) listEl.innerHTML = '<div class="sm-row" style="justify-content:center">Kon niet laden</div>';
    } finally {
      loading = false;
    }
  }

  // ====== Events ======
  // Openers
  document.addEventListener('click', (e) => {
    const opener = e.target.closest(OPENERS_SELECTOR);
    if (!opener) return;
    e.preventDefault();
    openDrawer();
    (async () => {
      if (!loaded) await loadFeed();
      applyFilter($inputEl()?.value || '');
    })();
  }, { passive: false });

  // Sluiten via knop/backdrop
  document.addEventListener('click', (e) => {
    if (e.target.closest('#sm-search-close')) { e.preventDefault(); requestClose(); }
    if (e.target.closest('#sm-search-backdrop')) requestClose();
  });

  // Kliks in het panel mogen niet naar de backdrop bubbelen,
  // MAAR laat de sluitknop wel werken.
  document.addEventListener('click', (e) => {
    const panel = $panel();
    if (!panel) return;

    const inPanel = panel.contains(e.target);
    const onClose = !!(e.target && e.target.closest('#sm-search-close'));
    if (inPanel && !onClose) {
      e.stopPropagation();   // bubble-phase: andere handlers blijven werken
    }
  }); // ← let it run in bubble phase (no third argument)

  // 🔽🔽🔽 NEW: Klik buiten panel sluit de search (alleen desktop)
  document.addEventListener('click', (e) => {
    const drawer = $drawer();
    const panel  = $panel();
    if (!drawer || !panel) return;
    if (!drawer.classList.contains('is-open')) return;

    // Niet buiten-klik sluiten als je op een opener klikt (anders: open→direct close)
    if (e.target.closest(OPENERS_SELECTOR)) return;

    // Alleen op desktop
    if (isMobile()) return;

    const clickedInsidePanel = panel.contains(e.target);
    if (!clickedInsidePanel) {
      requestClose();
    }
  });
  // 🔼🔼🔼 NEW END

  // ESC sluit
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && $drawer()?.classList.contains('is-open')) requestClose();
  });

  // Android / browser back -> sluit overlay i.p.v. pagina wissel
  window.addEventListener('popstate', () => {
    if ($drawer()?.classList.contains('is-open')) {
      closeDrawer({ fromPop: true });
      // geen verdere actie nodig; we blijven op dezelfde pagina
    }
  });

  // Live filter
  document.addEventListener('input', debounce((e) => {
    if (e.target && e.target.id === 'sm-search-input') {
      applyFilter(e.target.value || '');
    }
  }, 150));

  // Infinite scroll binnen de lijst
  document.addEventListener('scroll', () => {
    const listEl = $listEl();
    if (!listEl || !$drawer()?.classList.contains('is-open')) return;
    if (listEl.scrollTop + listEl.clientHeight >= listEl.scrollHeight - 120) renderMore();
  }, true);

  // Klikbare resultaten (single click navigatie)
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.sm-row');
    if (!link) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; // respecteer nieuwe tab etc.
    e.preventDefault();
    const href = link.getAttribute('href');
    requestClose();
    // klein tikje zodat de animatie start; daarna navigeren
    setTimeout(() => { window.location.assign(href); }, 80);
  });

  // Enter op gefocuste rij
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const focusedRow = document.activeElement?.closest('.sm-row');
    if (focusedRow) focusedRow.click();
  });
})();
